import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { AppConfig } from 'src/app/app.config';
import { HelpersService } from 'src/app/services/helpers.service'
import { JobcardService } from 'src/app/services/jobcard.service';

@Component({
  selector: 'app-workshop-jobcard-linked-services',
  templateUrl: './workshop-jobcard-linked-services.component.html',
  styleUrls: ['./workshop-jobcard-linked-services.component.scss'],
  providers: [MessageService]
})
export class WorkshopJobcardLinkedServicesComponent {

  @Input() linkedServices;
  @Input() linkableServices;
  @Input() jobCardID;
  @Output() jobSelected = new EventEmitter<any>();
  @Output() linkedServicesUpdated = new EventEmitter();
  @Output() favouriteCustomerChanged = new EventEmitter<number | null>();

  activeJobs: any = [];
  closedJobs: any = [];
  filteredActiveJobs: any = [];
  filteredClosedJobs: any = [];
  activeJobsOpen: boolean = true;
  closedJobsOpen: boolean = false;
  currencySymbol: string = '$';
  searchTerm: string = '';
  sortField: 'jobCardID' | 'customer' | 'scheduledDate' | '' = '';
  sortAsc: boolean = true;
  readonly sortOptions = [
    { field: 'jobCardID', label: 'bikeshop.modal.update_job_card.job_card.sort_job_number' },
    { field: 'customer', label: 'bikeshop.modal.update_job_card.job_card.sort_customer' },
    { field: 'scheduledDate', label: 'bikeshop.modal.update_job_card.job_card.sort_scheduled_date' }
  ];
  favouriteCustomerID: number | null = null;
  showMessaging: boolean = false;
  messagingJobcard: any = null;

  constructor(
    private appConfig: AppConfig,
    private helperService: HelpersService,
    private jobcardService: JobcardService,
    private messageService: MessageService,
    private translate: TranslateService
  ) { }

  ngOnInit() {
    this.filterJobs();
    this.currencySymbol = this.helperService.getPartnerData().CurrencySymbol || '$';
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['linkedServices']) {
      this.filterJobs();
      // The saved favourite is the customer that owns the linked jobs (0 means none)
      const owner = (this.linkedServices || []).map(job => job.linkedJobsOwner ?? job.LinkedJobsOwner).find(id => id);
      this.favouriteCustomerID = owner || null;
      this.applySearchFilter();
    }
  }

  filterJobs() {
    this.activeJobs = this.linkedServices.filter(job => job.statusID < 40);
    this.closedJobs = this.linkedServices.filter(job => job.statusID >= 40 && job.statusID != 100);
    
    // Apply search filter
    this.applySearchFilter();
  }

  applySearchFilter() {
    if (!this.searchTerm || this.searchTerm.trim() === '') {
      this.filteredActiveJobs = this.sortJobs([...this.activeJobs]);
      this.filteredClosedJobs = this.sortJobs([...this.closedJobs]);
      return;
    }

    const searchLower = this.searchTerm.toLowerCase().trim();
    
    this.filteredActiveJobs = this.sortJobs(this.activeJobs.filter(job => this.matchesSearchTerm(job, searchLower)));
    this.filteredClosedJobs = this.sortJobs(this.closedJobs.filter(job => this.matchesSearchTerm(job, searchLower)));
  }

  sortBy(field: any) {
    if (this.sortField === field) {
      this.sortAsc = !this.sortAsc;
    } else {
      this.sortField = field;
      this.sortAsc = true;
    }
    this.applySearchFilter();
  }

  private sortJobs(jobs: any[]): any[] {
    const dir = this.sortAsc ? 1 : -1;
    const byCustomer = (a, b) => (a.customer || '').localeCompare(b.customer || '', undefined, { sensitivity: 'base' });
    const byJobNumber = (a, b) => (Number(a.jobCardID) || 0) - (Number(b.jobCardID) || 0);
    return jobs.sort((a, b) => {
      // Favourite customer's jobs always come first, whatever the ordering
      const favourite = Number(this.isFavourite(b)) - Number(this.isFavourite(a));
      if (favourite !== 0) return favourite;
      switch (this.sortField) {
        case 'jobCardID':
          return byJobNumber(a, b) * dir;
        case 'scheduledDate':
          return ((a.scheduledDate ? new Date(a.scheduledDate).getTime() : 0) - (b.scheduledDate ? new Date(b.scheduledDate).getTime() : 0)) * dir
            || byJobNumber(a, b);
        default:
          // Default order (and customer sort): customer name, then job card number
          return (byCustomer(a, b) * (this.sortField ? dir : 1)) || byJobNumber(a, b);
      }
    });
  }

  private matchesSearchTerm(job: any, searchTerm: string): boolean {
    // Search across all relevant fields
    const searchableFields = [
      job.customer,
      job.jobCardID?.toString(),
      job.manufacturer,
      job.model,
      job.technician,
      job.scheduledDate,
      job.dateRequiredBy,
      job.duration?.toString(),
      job.invoiceTotal?.toString(),
      job.amountPaid?.toString(),
      this.getStatusLabel(job)
    ];

    return searchableFields.some(field =>
      field && field.toLowerCase().includes(searchTerm)
    );
  }

  onSearchChange() {
    this.applySearchFilter();
  }

  clearSearch() {
    this.searchTerm = '';
    this.applySearchFilter();
  }

  toggleSection(section: string, event: Event) {
    event.stopPropagation();
    if (section === 'ACTIVE_JOBS') {
      this.activeJobsOpen = !this.activeJobsOpen;
      this.closedJobsOpen = false;
    } else if (section === 'CLOSED_JOBS') {
      this.closedJobsOpen = !this.closedJobsOpen;
      this.activeJobsOpen = false;
    }
  }

  getStatusLabel(job) {
    return this.appConfig.getStatusLabel(job.statusID);
  }

  getStatusBackgroundStyle(job) {
    var status = this.appConfig.getStatusList().filter(status => status.key === job.statusID).pop();
    console.log(status);
    return status ? status.styleClass : '';
  }

  get hasMultipleCustomers(): boolean {
    return new Set((this.linkedServices || []).map(job => job.customerID)).size > 1;
  }

  isFavourite(job): boolean {
    return this.favouriteCustomerID != null && job.customerID === this.favouriteCustomerID;
  }

  // Jobs linked to an external system can't change the favourite
  hasExternalKey(job): boolean {
    const key = job.externalKey ?? job.ExternalKey;
    return key != null && String(key).trim() !== '';
  }

  // Only one favourite customer at a time; picking another customer's star replaces it
  toggleFavourite(job, event) {
    event.stopPropagation();
    if (this.isFavourite(job) || this.hasExternalKey(job)) return;

    const previousFavourite = this.favouriteCustomerID;
    this.favouriteCustomerID = job.customerID;
    this.applySearchFilter();
    this.jobcardService.setFavouriteLinkedServiceCustomer(job.partnerID, this.jobCardID, job.customerID).subscribe(() => {
      this.favouriteCustomerChanged.emit(this.favouriteCustomerID);
    }, () => {
      this.favouriteCustomerID = previousFavourite;
      this.applySearchFilter();
    });
  }

  get totalDuration(): number {
    return [...this.filteredActiveJobs, ...this.filteredClosedJobs].reduce((sum, job) => sum + (job.duration || 0), 0);
  }

  get totalAmount(): number {
    return [...this.filteredActiveJobs, ...this.filteredClosedJobs].reduce((sum, job) => sum + (job.invoiceTotal || 0), 0);
  }

  openMessaging(job, event) {
    event.stopPropagation();
    const customerName = (job.customer || '').trim();
    const firstSpace = customerName.indexOf(' ');
    // Shape the linked service like the jobcard the messaging component expects
    this.messagingJobcard = {
      id: job.id,
      jobCardID: job.jobCardID,
      partnerID: job.partnerID,
      customer: {
        ID: job.customerID,
        Name: firstSpace > -1 ? customerName.substring(0, firstSpace) : customerName,
        Surname: firstSpace > -1 ? customerName.substring(firstSpace + 1) : ''
      },
      selectedItem: { manufacturer: job.manufacturer, model: job.model }
    };
    this.showMessaging = true;
  }

  closeMessaging() {
    this.showMessaging = false;
    this.messagingJobcard = null;
  }

  selectJob(job) {
    this.jobSelected.emit(job.id);
  }

  // The primary customer's last remaining job can't be unlinked
  isLastFavouriteJob(job): boolean {
    return this.isFavourite(job) && this.linkedServices.filter(j => this.isFavourite(j)).length <= 1;
  }

  isUnlinkDisabled(job): boolean {
    return job.id == this.jobCardID || !!job.externalKey || this.isLastFavouriteJob(job);
  }

  unlinkJob(job, event) {
    event.stopPropagation();
    if (this.isUnlinkDisabled(job)) return;

    this.jobcardService.getLinkJobCard(this.linkedServices[0].partnerID, this.jobCardID, job.id).subscribe((results) => {
      this.linkedServicesUpdated.emit();
      this.messageService.add({ life: 5000, key: "all-linked-services-toast", detail: this.translate.instant(job.selected ? 'bikeshop.modal.update_job_card.job_card.link_service.success_text' : 'bikeshop.modal.update_job_card.job_card.unlink_service.success_text') });
    });
  }

}
